import { ISignUpFormData, SignUpForm } from './widgets/signup-form/SignUpForm'
import { SubmitHandler } from 'react-hook-form'
import { useEffect, useState } from 'react'
import {
  CompanySettingsForm,
  ICompanySettingsFormData,
} from './widgets/company-settings-form/CompanySettingsForm'
import { SignupArg, useSignupMutation } from '../../app/api'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../app/store/hooks/Redux'
import { selectOauthUser, selectUser } from '../../app/store/reducers/AuthSlice'
import { useTranslation } from 'react-i18next'
import { useCookies } from 'react-cookie'
import { AuthTemplate } from '../../widgets/utility/AuthTemplate'
import { ConfirmEmail } from './widgets/confirm-email/ConfirmEmail'

export const SignupPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAppSelector(selectUser)
  const oauthUser = useAppSelector(selectOauthUser)
  const [scenario, setScenario] = useState<'first' | 'second' | 'confirm-email'>('first')
  const [signUpFormValues, setSignUpFormValues] = useState<ISignUpFormData>()
  const [cookies] = useCookies()
  const [signup, { isLoading }] = useSignupMutation()

  const handleSubmitSignup: SubmitHandler<ISignUpFormData> = (formData: ISignUpFormData) => {
    const filteredCookies = Object.fromEntries(
      Object.entries(cookies).filter(([key]) =>
        [
          'utm_source',
          'utm_medium',
          'utm_campaign',
          'utm_term',
          'utm_content',
          'referer',
          'last_path',
        ].includes(key),
      ),
    )

    if (user) {
      setSignUpFormValues(formData)

      if (!user.is_email_verified && user.company_id === null) {
        setScenario('confirm-email')
      } else {
        setScenario('second')
      }
    } else {
      signup({
        body: {
          scenario: formData.scenario ? formData.scenario : scenario,
          ...formData,
          ...filteredCookies,
        } as SignupArg['body'],
      })
        .then((response: any) => {
          if (response.data?.success) {
            setSignUpFormValues(formData)

            if (
              !response.data?.data?.is_email_verified &&
              response.data?.data?.company_id === null
            ) {
              setScenario('confirm-email')
            } else {
              setScenario('second')
            }

            if (scenario === 'first') {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'signup_step_1_completed',
                properties: {
                  email: response.data.data.email,
                  full_name: response.data.data.fio,
                  user_id: response.data.data.id,
                  user_role: response.data.data.is_master ? 'owner' : response.data.data.sub_role,
                  employee:
                    response.data.data.email.includes('robusta.team') ||
                    response.data.data.email.includes('bluepear.net'),
                },
              })
            }
          } else {
            if (response.data?.errors && 'email' in response.data.errors) {
              navigate('/auth')
            }

            throw new Error()
          }
        })
        .then(async () => {
          const body = new FormData()

          body.append('fullName', formData.fullName || '')
          body.append('email', formData.email || '')
          body.append('utmMedium', filteredCookies.utm_medium || '')
          body.append('utmSource', filteredCookies.utm_source || '')
          body.append('utmReferer', filteredCookies.referer || '')
          body.append('utmCampaign', filteredCookies.utm_campaign || '')
          body.append('utmLastPath', filteredCookies.last_path || '')

          if (
            !formData.email?.includes('robusta.team') &&
            !formData.email?.includes('bluepear.co') &&
            !formData.email?.includes('bluepear.net')
          ) {
            await fetch(
              'https://sp1-nova.ru/api/site-integration/bluepearco.amocrm.ru/2a0a3482542/',
              {
                method: 'POST',
                body,
              },
            )
          }
        })
    }
  }

  const handleSubmitCompanySettings: SubmitHandler<ICompanySettingsFormData> = (
    formData: ICompanySettingsFormData,
  ) => {
    const filteredCookies = Object.fromEntries(
      Object.entries(cookies).filter(([key]) =>
        [
          'utm_source',
          'utm_medium',
          'utm_campaign',
          'utm_term',
          'utm_content',
          'referer',
          'last_path',
          'ref_cookie_id',
        ].includes(key),
      ),
    )

    signup({
      body: {
        ...signUpFormValues,
        ...formData,
        ...filteredCookies,
        scenario: 'second',
      } as SignupArg['body'],
    }).then((response: any) => {
      if (response.data?.success) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'signup_step_2_completed',
          properties: {
            user_id: response.data.data.id,
            company_id: response.data.data.company_id,
            company_website: formData.companyWebsite,
            company_role: formData.companyRole,
            company_name: formData.companyName,
            assistance_request: formData.assistanceRequest,
            contact_method: formData.contactMethod,
            contact: formData.contactDetails,
            plan_name: response.data.data.plan.name,
            plan_started_at: new Date(response.data.data.plan.started_at).toISOString(),
            plan_ends_at: new Date(response.data.data.plan.ends_at).toISOString(),
            billing_period: response.data.data.plan.billing_period,
            plan_status: response.data.data.plan.status,
            plan_type: response.data.data.plan.type,
            employee:
              response.data.data.email.includes('robusta.team') ||
              response.data.data.email.includes('bluepear.net'),
          },
        })

        navigate(response.data?.data?.allow_onboarding ? '/onboarding' : '/projects')
      }
    })
  }

  useEffect(() => {
    if (oauthUser && typeof oauthUser === 'object' && !oauthUser.error) {
      if (oauthUser.id && oauthUser.status === 'active') {
        navigate(oauthUser.allow_onboarding ? '/onboarding' : '/projects')
      } else if (oauthUser.id && oauthUser.status === 'uncompleted_registration') {
        if (!oauthUser.is_email_verified && oauthUser.company_id === null) {
          setScenario('confirm-email')
        } else {
          setScenario('second')
        }
      } else if (oauthUser.fullName && oauthUser.email) {
        handleSubmitSignup({
          fullName: oauthUser.fullName,
          email: oauthUser.email,
          scenario: 'first_oauth',
        })
      }
    }
  }, [user, oauthUser])

  return (
    <>
      <AuthTemplate
        form={
          {
            first: <SignUpForm isLoading={isLoading} submitHandler={handleSubmitSignup} />,
            second: (
              <CompanySettingsForm
                isLoading={isLoading}
                submitHandler={handleSubmitCompanySettings}
              />
            ),
            'confirm-email': <ConfirmEmail />,
          }[scenario]
        }
      >
        <div className={'flex h-full items-center justify-center px-[16px]'}>
          {
            {
              first: (
                <div className={'flex max-w-[430px] flex-col'}>
                  <div>
                    <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>
                      {t('pages.auth.affiliateCompliance')}
                    </h1>
                    <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                      {t('pages.auth.protectYourPPC')}
                    </p>
                  </div>
                  <div>
                    <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>
                      {t('pages.auth.competitorAnalysis')}
                    </h1>
                    <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                      {t('pages.auth.comprehensiveInsights')}
                    </p>
                  </div>
                </div>
              ),
              second: {
                bluepear: (
                  <div className={'flex max-w-[430px] flex-col'}>
                    <div>
                      <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>150+</h1>
                      <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                        Global brands trust us with their protection
                      </p>
                    </div>
                    <div>
                      <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>12K+</h1>
                      <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                        Affiliates detected on a monthly basis
                      </p>
                    </div>
                    <div>
                      <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>$1mil+</h1>
                      <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                        Dollars saved monthly
                      </p>
                    </div>
                  </div>
                ),
                robuscan: null,
                default: null,
              }[process.env.REACT_APP_PROJECT ?? 'default'],
              'confirm-email': {
                bluepear: (
                  <div className={'flex max-w-[430px] flex-col'}>
                    <div>
                      <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>150+</h1>
                      <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                        Global brands trust us with their protection
                      </p>
                    </div>
                    <div>
                      <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>12K+</h1>
                      <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                        Affiliates detected on a monthly basis
                      </p>
                    </div>
                    <div>
                      <h1 className={'pb-[16px] text-[24px] leading-[32px]'}>$1mil+</h1>
                      <p className='mb-[46px] text-[24px] leading-[36px] text-white'>
                        Dollars saved monthly
                      </p>
                    </div>
                  </div>
                ),
                robuscan: null,
                default: null,
              }[process.env.REACT_APP_PROJECT ?? 'default'],
            }[scenario]
          }
        </div>
      </AuthTemplate>
    </>
  )
}
